"use client";

/**
 * P3-08 · Shared add-to-cart action.
 *
 * Wraps the optimistic `useCart().addItem` with the cross-cutting concerns every
 * CTA needs: a pending flag, a success confirmation toast, the `cart.item_added`
 * analytics event, and — critically — an error toast on server rejection so the
 * buy path never fails silently. Returns whether the add succeeded so callers
 * (buy-now) can gate their follow-on navigation on it.
 */
import * as React from "react";

import { useFeatureFlag } from "~/components/feature-flags/provider";
import { mutationErrorMessage } from "~/lib/errors/mutation-error";
import { useToast } from "~/components/ui/toast";
import { track as capture } from "~/lib/analytics/client";
import type { AddItemInput } from "~/lib/cart/cart-interface";
import { useCart } from "~/lib/cart/use-cart";

export type AddToCartSource = "pdp" | "buy_now" | "sticky_bar";

export function useAddToCart(input: AddItemInput) {
  const { addItem, open } = useCart();
  const { toast } = useToast();
  const autoOpenMiniCart = useFeatureFlag("cartAutoOpenMiniCart");
  const [isAdding, setIsAdding] = React.useState(false);

  const { variantId, quantity, personalisation, giftCard } = input;

  const add = React.useCallback(
    async (
      source: AddToCartSource,
      opts?: { silentSuccess?: boolean },
    ): Promise<boolean> => {
      setIsAdding(true);
      try {
        await addItem({ variantId, quantity, personalisation, giftCard });
        // P9-01 · Funnel add-to-cart step (first-party capture).
        capture("ADD_TO_CART", { variantId, quantity });
        // The mini-cart drawer is the "you added it" confirmation (P4-02); when
        // it's off (or on buy-now's silent path) fall back to a toast. Buy-now
        // navigates away, so it never opens the drawer.
        if (!opts?.silentSuccess) {
          if (autoOpenMiniCart) {
            open();
          } else {
            toast({ title: "Added to bag", tone: "success" });
          }
        }
        return true;
      } catch (err) {
        toast({
          title: mutationErrorMessage(err, "Couldn't add to cart"),
          tone: "danger",
        });
        return false;
      } finally {
        setIsAdding(false);
      }
    },
    [
      addItem,
      open,
      autoOpenMiniCart,
      toast,
      variantId,
      quantity,
      personalisation,
      giftCard,
    ],
  );

  return { add, isAdding };
}
